import { CircularProgress } from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import AddShoppingCartOutlinedIcon from '@material-ui/icons/AddShoppingCartOutlined'
import CheckCircleOutlined from '@material-ui/icons/CheckCircleOutlined'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { orderSelectors } from 'ducks/orderComponents'
import {
  Badge,
  Box,
  Button,
  Chip,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  styled,
  Tooltip,
} from 'opensolar-ui'
import { useAddedToCartToast } from 'pages/ordering/addedToCartToast'
import { cartMatchesProjectBom, extractBomItemsFromSystemDesign } from 'pages/ordering/utils'
import { useNotify } from 'ra-core'
import { memo, useCallback, useMemo, useState } from 'react'
import { Link, useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { RootState } from 'types/state'
import { useDateFormatter } from 'util/date'
import type { HardwareOrderProjectRecord, InventoryTransferType, SystemDesignType } from '../../type'
import type { ProjectTablePresenter } from '../projectTablePresenter'
import type { ProjectTableStore } from '../useProjectTable'
import ContactsCell from './ContactsCell'
import { SystemBomPreviewPopover } from './SystemBomPreviewPopover'
import SystemSelectDropdown from './SystemSelectDropdown'

const isOrderableItem = (item: unknown): boolean => {
  if (typeof item !== 'object' || item == null) return false
  const parsed = item as { code?: string; component_code?: string; quantity?: number }
  const hasCode = !!(parsed.code ?? parsed.component_code)
  return hasCode && typeof parsed.quantity === 'number' && parsed.quantity > 0
}

const systemHasBomData = (systemDesign: SystemDesignType): boolean => {
  const bomLines = systemDesign.bom_line_items
  if (Array.isArray(bomLines) && bomLines.length > 0 && bomLines.some(isOrderableItem)) return true
  const categories: Array<'batteries' | 'inverters' | 'modules' | 'others'> = [
    'batteries',
    'inverters',
    'modules',
    'others',
  ]
  for (const category of categories) {
    const items = systemDesign[category]
    if (Array.isArray(items) && items.length > 0 && items.some(isOrderableItem)) return true
  }
  return false
}

type ProjectTableRowPropsType = {
  checked: boolean
  record: HardwareOrderProjectRecord
  projectTableStore: ProjectTableStore
  projectTablePresenter: ProjectTablePresenter
  allowEdit?: boolean
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  fontSize: '14px',
  color: theme.palette.grey[900],
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  verticalAlign: 'top',
}))

const CheckboxCell = styled(TableCell)(({ theme }) => ({
  padding: '0 0 0 16px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  width: '56px',
}))

const ActionCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  width: '180px',
  minWidth: '180px',
  whiteSpace: 'normal',
  verticalAlign: 'top',
}))

const ActionCellButtons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: '8px',
})

const AddToCartButtonStyled = styled(Button)(({ theme }) => ({
  minWidth: 160,
  width: 160,
  backgroundColor: `${theme.palette.neutralGrey[200]} !important`,
  color: `${theme.palette.neutralGrey[1000]} !important`,
  '&:hover': {
    backgroundColor: `${theme.palette.neutralGrey[300]} !important`,
  },
  '&.Mui-disabled': {
    backgroundColor: `${theme.palette.common.white} !important`,
    color: `${theme.palette.grey[400]} !important`,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
}))

const ActionCellLoading = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 42,
  height: 42,
})

const CheckboxWrapper = styled('span')<{ $readOnly?: boolean }>(({ $readOnly }) => ({
  display: 'inline-flex',
  ...($readOnly && {
    cursor: 'not-allowed',
    '& *': { pointerEvents: 'none' },
  }),
}))

const LoadingCheckbox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 42,
  height: 42,
})

const AddressContainer = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '8px',
})

const AddressWrapper = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  minWidth: 0,
})

const AddressLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 500,
})

const AddressText = styled('div')(({ theme }) => ({
  fontSize: theme.typography.subtitle2.fontSize,
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
}))

const BusinessName = styled('span')(({ theme }) => ({
  color: theme.palette.grey[600],
}))

// Local address display without tooltip (title attribute)
const AddressName = ({ business_name, address }: { business_name?: string; address?: string }) => {
  const translate = useTranslate()
  const addressResolved = address || translate('(Address not specified)')

  return (
    <AddressText>
      {addressResolved}
      {business_name && (
        <>
          <br />
          <BusinessName>{business_name}</BusinessName>
        </>
      )}
    </AddressText>
  )
}

const SoldStatusBadge = styled(Badge)({
  '&.OSUI-Badge-root': {
    backgroundColor: '#E1FAEA',
    color: '#016626',
    fontSize: 12,
    fontWeight: 400,
    height: 20,
    padding: '0 6px',
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
  },
  '& .MuiSvgIcon-root': {
    width: 14,
    height: 14,
    color: '#016626',
  },
})

const OrderedStatusChip = styled(Chip)({
  cursor: 'pointer',
  height: 'auto',
  '&:hover': {
    opacity: 0.8,
  },
  '& .MuiChip-label, & .OSUI-Chip-label': {
    paddingLeft: 7,
    paddingRight: 7,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
})

const PartiallyOrderedStatusChip = styled(Chip)({
  cursor: 'pointer',
  height: 'auto',
  backgroundColor: '#FDF4F2',
  color: '#B84E1C',
  '&:hover': {
    opacity: 0.8,
    backgroundColor: '#FDF4F2',
  },
  '& .MuiChip-label, & .OSUI-Chip-label': {
    paddingLeft: 7,
    paddingRight: 7,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
})

const FailedStatusChip = styled(Chip)({
  cursor: 'pointer',
  height: 'auto',
  backgroundColor: '#FFE3E3',
  color: '#961616',
  '&:hover': {
    opacity: 0.8,
    backgroundColor: '#FFE3E3',
  },
  '& .MuiChip-label, & .OSUI-Chip-label': {
    paddingLeft: 7,
    paddingRight: 7,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
})

const StatusChipWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '4px',
})

const PartiallyOrderedInfoIcon = styled(InfoOutlined)({
  color: '#B84E1C',
  fontSize: 18,
  cursor: 'pointer',
})

const FailedInfoIcon = styled(InfoOutlined)({
  color: '#961616',
  fontSize: 18,
  cursor: 'pointer',
})

const SystemOptionContainer = styled(Box, { name: 'SystemOptionContainer' })({
  display: 'flex',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: 1,
})

const SystemOptionDropdownContainer = styled(Box, { name: 'SystemOptionDropdownContainer' })({
  minWidth: 0,
  flex: '1 1 120px',
  maxWidth: '100%',
})

type OrderStatus = 'ordered' | 'partially_ordered' | 'failed'

type OrderStatusResult = {
  status: OrderStatus
  displayCount?: number
  totalCount?: number
} | null

const getOrderStatus = (
  orders: HardwareOrderProjectRecord['orders'],
  inventoryTransfers?: InventoryTransferType[]
): OrderStatusResult => {
  // Use orders if available, otherwise fallback to inventory_transfers
  const ordersToCheck = orders && orders.length > 0 ? orders : inventoryTransfers

  if (!ordersToCheck || ordersToCheck.length === 0) {
    return null
  }

  const totalCount = ordersToCheck.length
  const successfulCount = ordersToCheck.filter((order) => order.status === 'successful').length

  // Single supplier order (successful) - exactly 1 order with status "successful"
  if (totalCount === 1 && successfulCount === 1) {
    return { status: 'ordered' }
  }

  // Single supplier order (failed) - exactly 1 order with status "failed"
  if (totalCount === 1 && successfulCount === 0) {
    return { status: 'failed' }
  }

  // Multiple supplier orders (all successful)
  if (totalCount > 1 && successfulCount === totalCount) {
    return { status: 'ordered', displayCount: successfulCount, totalCount }
  }

  // Multiple supplier orders (all failed)
  const failedCount = totalCount - successfulCount
  if (totalCount > 1 && successfulCount === 0) {
    return { status: 'failed', displayCount: failedCount, totalCount }
  }

  // Multiple supplier orders (mixed success/failure)
  if (totalCount > 1 && successfulCount > 0 && successfulCount < totalCount) {
    return { status: 'partially_ordered', displayCount: successfulCount, totalCount }
  }

  return null
}

const ProjectTableRow = ({
  checked,
  record,
  projectTableStore,
  projectTablePresenter,
  allowEdit = true,
}: ProjectTableRowPropsType) => {
  const history = useHistory()
  const notify = useNotify()
  const translate = useTranslate()
  const { showAddedToCartToast } = useAddedToCartToast()
  const formatDate = useDateFormatter({ variant: 'date' })

  const calcErrors = record.calculation_error_messages && record.calculation_error_messages.length > 0
  const systemOptions = projectTablePresenter.getSystemSelectOptions(record.systems)
  const selectedSystemId = projectTablePresenter.getSystemSelectedId({
    projectId: record.id,
    soldSystemId: record.system_sold_id,
    selectedSystemMap: projectTableStore.selectedSystemMap,
    systemOptions,
  })

  const [noBomWarningShownForSystemId, setNoBomWarningShownForSystemId] = useState<number | null>(null)

  const isLoading = useMemo(() => {
    return selectedSystemId ? projectTableStore.loadingSystemIds.has(selectedSystemId) : false
  }, [projectTableStore.loadingSystemIds, selectedSystemId])

  const [selectedSystemHasBom, bomItems] = useMemo((): [boolean, { code: string; quantity: number }[]] => {
    if (!selectedSystemId || !record.systems?.length) return [false, []]
    const systemDesign = record.systems.find((s) => s.id === selectedSystemId)
    if (!systemDesign) return [false, []]
    return [systemHasBomData(systemDesign), extractBomItemsFromSystemDesign(systemDesign)]
  }, [record.systems, selectedSystemId])

  const cartLineItems = useSelector((state: RootState) =>
    selectedSystemId != null
      ? orderSelectors.getCartLineItemsForProjectAndSystem(state, record.id, selectedSystemId)
      : []
  )
  const hasCurrentSystemInCart = cartLineItems.length > 0

  type ButtonState = 'add' | 'in_cart' | 'update'
  const buttonState: ButtonState = useMemo(() => {
    if (!hasCurrentSystemInCart) return 'add'
    if (selectedSystemId != null && cartMatchesProjectBom(cartLineItems, bomItems, record.id, selectedSystemId))
      return 'in_cart'
    return 'update'
  }, [hasCurrentSystemInCart, cartLineItems, bomItems, record.id, selectedSystemId])

  const hasNoBomWarningBeenShown = selectedSystemId != null && noBomWarningShownForSystemId === selectedSystemId

  const handleAddOrUpdateCart: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.stopPropagation()

    if (!allowEdit || !selectedSystemId || isLoading || !selectedSystemHasBom) {
      return
    }

    if (calcErrors) {
      notify(
        translate(
          'This project cannot be selected for review as the BOM may not be fully updated due to calculation error. Please review the design before ordering.'
        ),
        'error'
      )
      return
    }

    let result: { itemCount: number; subtotal: number } | null | undefined

    if (buttonState === 'update') {
      result = await projectTablePresenter.updateCartForSystem({
        projectId: record.id,
        systemId: selectedSystemId,
        record,
      })
    } else {
      const toggleResult = await projectTablePresenter.onToggle({
        projectId: record.id,
        systemId: selectedSystemId,
        record,
      })
      if (!toggleResult.didChange) {
        setNoBomWarningShownForSystemId(selectedSystemId)
        notify(
          translate(
            'This project has no BOM data for the selected system. Please ensure the design has components before adding to order.'
          ),
          'warning'
        )
        return
      }
      result = toggleResult.added ?? null
    }

    if (result) {
      const projectLabel = record.address || record.business_name || translate('Project')
      showAddedToCartToast({
        variant: 'project',
        project: {
          label: projectLabel,
          itemCount: result.itemCount,
          subtotal: result.subtotal,
          location: 'shop',
        },
      })
    }

    logAmplitudeEvent('hardware_project_checkbox_toggled', {
      activate: 'on',
    })
  }

  const onSelectSystemDropdown = useCallback(
    (systemId: number) => {
      projectTablePresenter.handleSelectSystem({
        currentSystemId: selectedSystemId,
        systemId,
        projectId: record.id,
        record,
      })
    },
    [record.id, selectedSystemId]
  )

  const orderStatus = getOrderStatus(record.orders, record.inventory_transfers)
  const hasSoldSystem = record.system_sold_id != null

  const noSystemOrNoBom = !selectedSystemHasBom || hasNoBomWarningBeenShown
  const addToCartTooltipTitle =
    buttonState === 'in_cart'
      ? translate('Items from this project added to cart')
      : buttonState === 'update'
        ? translate("Items in cart don't match project")
        : noSystemOrNoBom
          ? systemOptions.length === 0
            ? translate("This project doesn't have a system option yet.")
            : translate("This system option doesn't have any components yet.")
          : ''

  const isReviewCartDisabled = !selectedSystemId || systemOptions.length === 0

  const isCheckboxDisabled = !allowEdit || systemOptions.length === 0 || !selectedSystemId || !selectedSystemHasBom
  const isAddToCartDisabled =
    isCheckboxDisabled ||
    buttonState === 'in_cart' ||
    hasCurrentSystemInCart ||
    hasNoBomWarningBeenShown ||
    orderStatus?.status === 'failed' ||
    orderStatus?.status === 'partially_ordered'

  return (
    <StyledTableRow data-testid="order-by-project-row">
      {/* Checkboxes removed from multi-project order table
      <CheckboxCell>
        {isLoading ? (
          <LoadingCheckbox>
            <CircularProgress size={20} />
          </LoadingCheckbox>
        ) : (
          <CheckboxWrapper $readOnly={isCheckboxDisabled}>
            <Checkbox
              color="primary"
              checked={checked}
              disabled={isCheckboxDisabled}
              onClick={handleAddOrUpdateCart}
              id="hardware-ordering-project-select"
            />
          </CheckboxWrapper>
        )}
      </CheckboxCell>
      */}
      <StyledTableCell>
        <AddressContainer>
          <AddressLink to={`/projects/${record.id}/manage`}>
            <AddressWrapper style={{ alignItems: record.business_name ? 'flex-start' : 'center' }}>
              <AddressName business_name={record.business_name} address={record.address} />
            </AddressWrapper>
          </AddressLink>
          {hasSoldSystem && (
            <SoldStatusBadge size="small" startIcon={<CheckCircleOutlined />} label={translate('Sold')} />
          )}
        </AddressContainer>
      </StyledTableCell>
      <StyledTableCell>
        <ContactsCell contactsData={record.contacts_data} />
      </StyledTableCell>
      <StyledTableCell>
        <SystemOptionContainer>
          <SystemOptionDropdownContainer>
            <SystemSelectDropdown
              selectedSystemId={selectedSystemId}
              systemOptions={systemOptions}
              onSelect={onSelectSystemDropdown}
              disabled={!allowEdit}
            />
          </SystemOptionDropdownContainer>
          <SystemBomPreviewPopover
            record={record}
            selectedSystemId={selectedSystemId}
            projectTablePresenter={projectTablePresenter}
            disabled={isReviewCartDisabled}
          />
        </SystemOptionContainer>
      </StyledTableCell>
      <StyledTableCell>
        {orderStatus?.status === 'ordered' ? (
          <OrderedStatusChip
            color="success"
            label={
              orderStatus.totalCount && orderStatus.totalCount > 1
                ? `Ordered (${orderStatus.displayCount}/${orderStatus.totalCount})`
                : 'Ordered'
            }
            size="small"
            onClick={() => history.push(`/projects/${record.id}/shop`)}
          />
        ) : orderStatus?.status === 'partially_ordered' ? (
          <StatusChipWrapper>
            <PartiallyOrderedStatusChip
              label={
                orderStatus.totalCount
                  ? `Partially Ordered (${orderStatus.displayCount}/${orderStatus.totalCount})`
                  : 'Partially Ordered'
              }
              size="small"
              onClick={() => history.push(`/projects/${record.id}/shop`)}
            />
            <Tooltip
              title="There was an error with your order for this project. View the project details for more information."
              arrow
              placement="top"
            >
              <PartiallyOrderedInfoIcon />
            </Tooltip>
          </StatusChipWrapper>
        ) : orderStatus?.status === 'failed' ? (
          orderStatus.totalCount && orderStatus.totalCount > 1 ? (
            <StatusChipWrapper>
              <FailedStatusChip
                label={`Failed (${orderStatus.displayCount}/${orderStatus.totalCount})`}
                size="small"
                onClick={() => history.push(`/projects/${record.id}/shop`)}
              />
              <Tooltip
                title="There was an error with your order for this project. View the project details for more information."
                arrow
                placement="top"
              >
                <FailedInfoIcon />
              </Tooltip>
            </StatusChipWrapper>
          ) : (
            <FailedStatusChip label="Failed" size="small" onClick={() => history.push(`/projects/${record.id}/shop`)} />
          )
        ) : (
          '－'
        )}
      </StyledTableCell>
      <StyledTableCell>{formatDate(record.installation_date) || '－'}</StyledTableCell>
      <ActionCell>
        <ComponentVersionsInherit versions={ComponentVersions_3_0}>
          <ActionCellButtons>
            {isLoading ? (
              <ActionCellLoading>
                <CircularProgress size={20} />
              </ActionCellLoading>
            ) : (
              <Tooltip title={addToCartTooltipTitle} arrow placement="top">
                <span>
                  <AddToCartButtonStyled
                    variant="contained"
                    size="small"
                    disableElevation
                    disabled={isAddToCartDisabled}
                    onClick={handleAddOrUpdateCart}
                    id="hardware-ordering-project-select"
                    startIcon={buttonState !== 'in_cart' ? <AddShoppingCartOutlinedIcon /> : undefined}
                  >
                    {buttonState === 'in_cart'
                      ? translate('Added to Cart')
                      : buttonState === 'update'
                        ? translate('Update Cart')
                        : translate('Add to Cart')}
                  </AddToCartButtonStyled>
                </span>
              </Tooltip>
            )}
          </ActionCellButtons>
        </ComponentVersionsInherit>
      </ActionCell>
    </StyledTableRow>
  )
}

export default memo(ProjectTableRow)
